// Auto-growing textarea helper.
//
// Message composers start at one or two rows and should grow with the text as
// the user types, then stop at a sensible cap (~8–10 rows) and scroll
// internally — so a long message stays readable while composing without the
// box ever taking over the screen. Shrinks back as content is removed.
//
// Used by every message composer (customer record reply, Messages page reply +
// compose modal, customer-compose modal, dashboard message popups) so the feel
// is identical everywhere. Mirrors the inline pattern used by the Kit panel.

/** Default cap in pixels — roughly 9 rows at the composer's line-height. */
const DEFAULT_MAX_PX = 200;

/**
 * Make a textarea grow/shrink to fit its content up to `maxPx`, after which it
 * scrolls internally. Sizes immediately (so a restored draft is shown at the
 * right height) and returns a disposer that removes the listener.
 *
 * Enter-to-send handlers are untouched — this only listens to `input`.
 */
export function attachAutoGrow(el: HTMLTextAreaElement, maxPx: number = DEFAULT_MAX_PX): () => void {
  // Border-box makes the scrollHeight-based height exact regardless of the
  // element's padding/border (the app has no global box-sizing reset).
  el.style.boxSizing = "border-box";
  // No manual drag-handle — height is driven entirely by content. Override any
  // CSS-default `resize`/`min-height` so the box can shrink to one row and the
  // JS height takes full effect.
  el.style.resize = "none";
  el.style.minHeight = "0";
  const resize = (): void => {
    // Collapse first so scrollHeight reflects the true content height (not the
    // current, possibly-too-tall box), then grow up to the cap.
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, maxPx);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxPx ? "auto" : "hidden";
  };
  el.addEventListener("input", resize);
  // Some surfaces type via paste/programmatic set without firing `input`;
  // `change` (blur) is a cheap safety net to re-fit.
  el.addEventListener("change", resize);
  // Size once now (covers pre-filled / restored-draft values). Defer one frame
  // too, so an element measured before layout (e.g. inside a just-shown dialog
  // or freshly-rendered card) still gets a correct scrollHeight.
  resize();
  requestAnimationFrame(resize);
  return () => {
    el.removeEventListener("input", resize);
    el.removeEventListener("change", resize);
  };
}

/**
 * Reset a textarea back to its single-row height — call after the value has
 * been cleared programmatically (e.g. just after a message sends), since
 * clearing `.value` in code does not fire an `input` event.
 */
export function resetAutoGrow(el: HTMLTextAreaElement): void {
  el.style.height = "auto";
  el.style.overflowY = "hidden";
}
